/**
 * @file add-new-address.tsx
 * @module app/modals/add-new-address
 * @description Modal for adding or editing a customer delivery address in UTrade Admin.
 * Handles address autocomplete, district resolution, and save/update submission.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.2.1
 */

import {
  Modal,
  Button,
  Row,
  Col,
  Card,
  FormLabel,
  Form,
  InputGroup,
} from 'react-bootstrap';
import CrossSvg from '../../_admin/assets/media/close.png';
import { CustomSelectWhite } from '../custom/Select/CustomSelectWhite';
import { useEffect, useState } from 'react';
import APICallService from '../../api/apiCallService';
import { buyer, customerService, master } from '../../api/apiEndPoints';
import clsx from 'clsx';
import AutoComplete from '../custom/autoComplete';
import { searchGoogleAddressUsingLatLong } from '../../utils/googleAddress';
import Validations from '../../utils/validations';
import { success } from '../../Global/toast';
import { useWarehouse } from '../context/WarehouseContext';
const AddNewAddress = (props: any) => {
  const { selectedWarehouse } = useWarehouse();
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState<{
    name: string;
    phoneCountry: string;
    phone: string;
    addressLine1: string;
    landmark: string;
    city: string;
    district: string;
    districtName: string;
    lat: string;
    lng: string;
    ward: string;
    wardName: string;
  }>({
    name: props.details?.name,
    phoneCountry: '+255',
    phone: props.details?.phone ? props.details?.phone : props?.phone,
    addressLine1: props.details?.addressLine1,
    districtName: props.details?.districtName,
    landmark: props.details?.landmark,
    city: props.details?.city,
    district: props.details?.district,
    lat: props.details?.lat,
    lng: props.details?.lng,
    ward: props.details?.ward,
    wardName: props.details?.wardName,
  });

  const [districts, setDistricts] = useState([]);
  const [wards, setWards] = useState<any[]>([]);
  const [validation, setValidation] = useState<{
    name: boolean;
    phoneCountry: boolean;
    phone: boolean;
    addressLine1: boolean;
    landmark: boolean;
    city: boolean;
    district: boolean;
    ward: boolean;
  }>({
    name: false,
    phoneCountry: false,
    phone: false,
    addressLine1: false,
    landmark: false,
    city: false,
    district: false,
    ward: false,
  });
  const [fetchLoader, setFetchLoader] = useState(false);
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    (async () => {
      setFetchLoader(true);
      await fetchDistrict();
      setFetchLoader(false);
    })();
  }, []);

  useEffect(() => {
    if (address?.district) {
      fetchWards(address.district);
    } else {
      setWards([]);
    }
  }, [address?.district]);

  useEffect(() => {
    if (!address?.ward || !wards.length) return;
    const isWardIdSelected = wards.some((ward: any) => ward._id === address.ward);
    if (!isWardIdSelected) {
      const matchedWard = wards.find((ward: any) => ward.name === address.ward);
      if (matchedWard?._id) {
        setAddress((prev: any) => ({
          ...prev,
          ward: matchedWard._id,
          wardName: matchedWard.name || '',
        }));
      }
    } else if (!address?.wardName) {
      const selectedWard = wards.find((ward: any) => ward._id === address.ward);
      if (selectedWard?.name) {
        setAddress((prev: any) => ({
          ...prev,
          wardName: selectedWard.name,
        }));
      }
    }
  }, [address?.ward, wards]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const fetchDistrict = async () => {
    let params: any = {};
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      customerService.listDistrict,
      params,
      '',
      '',
      '',
      '',
      props.moduleName,
      selectedWarehouse?.value || null
    );
    let response = await apiService.callAPI();
    let data: any = [...districts];
    response.records.forEach((val: any) => {
      data.push({
        value: val._id,
        label: val.name,
        _id: val._id,
        name: val.name,
      });
    });
    setDistricts(data);
  };

  const fetchWards = async (districtId: string) => {
    if (!districtId) {
      setWards([]);
      return [];
    }

    const params: any = {
      districts: [districtId],
      needCount: false,
    };

    const apiService = new APICallService(
      master.listWards,
      params,
      '',
      '',
      '',
      '',
      props.moduleName,
      selectedWarehouse?.value || null
    );

    const response = await apiService.callAPI();
    const records = (response?.records || []).map((ward: any) => ({
      value: ward._id,
      label: ward.name,
      _id: ward._id,
      name: ward.name,
    }));
    setWards(records);
    return records;
  };

  const handleChange = (value: any, name: string) => {
    let temp: any = { ...address };
    let validateTemp: any = { ...validation };
    temp[name] = value.trimStart();
    if (value.trimStart().length > 0) {
      validateTemp[name] = false;
    } else {
      validateTemp[name] = true;
    }
    setAddress(temp);
    setValidation(validateTemp);
  };
  const handleDistrict = async (event: any) => {
    let temp: any = { ...address };
    let tempValidation: any = { ...validation };
    temp.district = event._id;
    temp.districtName = event.label;
    temp.ward = '';
    temp.wardName = '';
    tempValidation.district = false;
    tempValidation.ward = true;
    setValidation(tempValidation);
    setAddress(temp);
  };

  const handleWard = (event: any) => {
    let temp: any = { ...address };
    let tempValidation: any = { ...validation };
    temp.ward = event?._id || '';
    temp.wardName = event?.label || event?.name || '';
    tempValidation.ward = !(event?._id || '').length;
    setValidation(tempValidation);
    setAddress(temp);
  };
  const handleSaveAddress = async () => {
    let temp = { ...address };
    let validateTemp = { ...validation };
    if (
      !temp.name &&
      !temp.phone &&
      !temp.addressLine1 &&
      !temp.city &&
      !temp.district &&
      !temp.ward
    ) {
      validateTemp.name = true;
      validateTemp.phone = true;
      validateTemp.addressLine1 = true;
      validateTemp.city = true;
      validateTemp.district = true;
      validateTemp.ward = true;
    } else if (!temp.name) {
      validateTemp.name = true;
    } else if (!temp.phone) {
      validateTemp.phone = true;
    } else if (!temp.addressLine1) {
      validateTemp.addressLine1 = true;
    } else if (!temp.landmark) {
      validateTemp.landmark = true;
    } else if (!temp.city) {
      validateTemp.city = true;
    } else if (!temp.district) {
      validateTemp.district = true;
    } else if (!temp.ward) {
      validateTemp.ward = true;
    } else {
      setLoading(true);
      try {
        // Use the filter method to find matching objects
        const matchingObjects: any = districts.filter((obj: any) => {
          return obj.name.includes(temp.districtName);
        });
        if (matchingObjects.length) {
          temp.districtName = matchingObjects[0].name;
          temp.district = matchingObjects[0]._id;
        } else {
          temp.districtName = 'UpCountry';
          temp.district = '651d5fa8317c6e0dcdcd8fa1';
        }

        if (temp.ward && !temp.wardName) {
          const selectedWard = wards.find((ward: any) => ward._id === temp.ward);
          if (selectedWard?.name) {
            temp.wardName = selectedWard.name;
          }
        }

        let apiService = new APICallService(
          props.isEdit ? buyer.updateAddress : buyer.addAddress,
          temp,
          {
            _id: props.selectedCustomer,
            ...(props.isEdit && { deliveryAddress: props.details._id }),
          },
          '',
          '',
          '',
          props.moduleName,
          selectedWarehouse?.value || null
        );
        let response = await apiService.callAPI();
        if (response && !response.error) {
          success(
            props.isEdit
              ? 'Delivery Address has been updated successfuly'
              : 'Delivery Address has been added successfuly'
          );
          props.onSaveAddress(response);
        }
      } catch (err) {
        console.error('Error saving address:', err);
      } finally {
        setLoading(false);
      }
    }
    setValidation(validateTemp);
  };

  /**
   * Handles the change of address input by the user. It searches for the address using
   * Google Maps API and updates the address data accordingly.
   *
   * @param value The address input by the user
   * @param lat The latitude of the address
   * @param lng The longitude of the address
   */
  const handleAddressChange = async (value: string, lat: any, lng: any) => {
    const googleAddress = await searchGoogleAddressUsingLatLong(lat, lng, {
      searchText: value,
      districts,
    });
    const selectedAddress = googleAddress?.data?.[0];
    const normalizedAddress = value.trimStart();
    const districtId = selectedAddress?.district || '651d5fa8317c6e0dcdcd8fa1';
    const wardOptions = await fetchWards(districtId);
    const matchedWard = wardOptions.find(
      (ward: any) => ward.name === selectedAddress?.wardName
    );

    // Update the address state with the new values, ensuring that if any of the selectedAddress properties are undefined, we provide a default value.
    setAddress((prev: any) => ({
      ...prev,
      ward: matchedWard?._id || '',
      wardName: matchedWard?.name || '',
      city: selectedAddress?.localityName || '',
      districtName: selectedAddress?.districtName || 'UpCountry',
      district: districtId,
      addressLine1: normalizedAddress,
      lat,
      lng,
    }));

    // Update the validation state based on the presence of selectedAddress and normalizedAddress
    setValidation((prev: any) => ({
      ...prev,
      district: !selectedAddress?.status,
      addressLine1: !normalizedAddress.length,
      ward: !matchedWard?._id,
    }));
  };

  const handleOnKeyPress = async (event: any) => {
    const key = event.key;
    let validations = await Validations.allowOnlyNumbers(key);
    if (!validations) {
      event.preventDefault();
      return false;
    }
    return true;
  };

  return (
    <Modal
      {...props}
      show={props.show}
      onHide={props.onHide}
      dialogClassName="modal-dialog-centered min-w-lg-794px"
      className="border-r10px"
      centered
      backdrop="static"
    >
      <Modal.Header className="border-bottom-0 pb-0 text-center mx-auto">
        <img
          className="close-inner-top-2"
          width={40}
          height={40}
          src={CrossSvg}
          alt="closebutton"
          onClick={props.onHide}
        />
        <Modal.Title className="fs-26 fw-bolder mw-lg-375px pt-lg-3">
          {props.isEdit ? 'Edit Address' : 'Add new address'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card className="bg-f9f9f9 border">
          <Card.Body>
            <Row className="g-6">
              <Col md={6}>
                <FormLabel className="fs-16 fw-500 text-dark">
                  Shop name
                </FormLabel>
                <Form.Control
                  className={clsx(
                    'form-control-custom bg-white',
                    validation.name ? 'border-danger' : ''
                  )}
                  value={address.name}
                  onChange={(event: any) => {
                    handleChange(event.target.value, 'name');
                  }}
                />
              </Col>
              <Col md={6}>
                <FormLabel className="fs-16 fw-500 text-dark">
                  Phone number
                </FormLabel>
                <InputGroup
                  className={clsx(
                    validation.phone ? 'border border-danger' : ''
                  )}
                >
                  <InputGroup.Text className="bg-white border-right-0 fs-16 fw-600 text-black px-6">
                    +255
                  </InputGroup.Text>
                  <Form.Control
                    name="phoneNumber"
                    className="form-control-custom border-active-none bg-white border-left-0 ps-0"
                    aria-label="Default"
                    onChange={async (event: any) => {
                      handleChange(event.target.value, 'phone');
                    }}
                    onKeyPress={(event: any) => {
                      handleOnKeyPress(event);
                    }}
                    value={address.phone}
                    aria-describedby="inputGroup-sizing-default"
                  />
                </InputGroup>
              </Col>
              <Col md={12}>
                <FormLabel className="fs-16 fw-500 text-dark">
                  Address line 1
                </FormLabel>
                <AutoComplete
                  address={address.addressLine1}
                  handleAddressChange={handleAddressChange}
                  lat={address.lat}
                  lng={address.lng}
                  border={clsx(validation.addressLine1 ? '#e55451' : '')}
                />
              </Col>
              <Col md={6}>
                <FormLabel className="fs-16 fw-500 text-dark">
                  Landmark
                </FormLabel>
                <Form.Control
                  className={clsx(
                    'form-control-custom bg-white',
                    validation.landmark ? 'border-danger' : ''
                  )}
                  value={address.landmark}
                  onChange={(event: any) => {
                    handleChange(event.target.value, 'landmark');
                  }}
                />
              </Col>
              <Col md={6}>
                <FormLabel className="fs-16 fw-500 text-dark">City</FormLabel>
                <Form.Control
                  className={clsx(
                    'form-control-custom bg-white',
                    validation.city ? 'border-danger' : ''
                  )}
                  onChange={(event: any) => {
                    handleChange(event.target.value, 'city');
                  }}
                  value={address.city}
                />
              </Col>
              <Col md={6}>
                <FormLabel className="fs-16 fw-500 text-dark">
                  District
                </FormLabel>
                <CustomSelectWhite
                  border={validation.district ? '#e55451' : ''}
                  disabled={loading}
                  isLoading={fetchLoader}
                  default={districts[0] ? districts[0] : []}
                  options={districts}
                  loadingMessage={'Fetching Data'}
                  value={districts.filter((value: any) => {
                    return address.district === value._id;
                  })}
                  onChange={(e: any) => handleDistrict(e)}
                />
              </Col>
              <Col md={6}>
                <FormLabel className="fs-16 fw-500 text-dark">Ward</FormLabel>
                <CustomSelectWhite
                  border={validation.ward ? '#e55451' : ''}
                  isDisabled={!address.district || loading}
                  default={wards[0] ? wards[0] : []}
                  options={wards}
                  loadingMessage={'Fetching Data'}
                  value={wards.filter((value: any) => {
                    return address.ward === value._id || address.ward === value.name;
                  })}
                  onChange={(e: any) => handleWard(e)}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer className="justify-content-center mb-4 border-top-0">
        <Button className='uw-btn uw-btn-primary' onClick={handleSaveAddress}>
          {!loading && (
            <span className="indicator-label">
              {props.isEdit ? 'Update address' : 'Add address'}
            </span>
          )}
          {loading && (
            <span className="indicator-progress" style={{ display: 'block' }}>
              Please wait...
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
export default AddNewAddress;
